import { Timer, Pencil } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';



import { useProviderCompany } from "../../../../../context/ProviderCompanyContext";
import { updateProviderTimeKeepingPreferences } from '../../../../../services/staffingService';

const timeKeepingSchema = yup.object().shape({
    checkInStart: yup.number().min(0).max(60).required('Check-in start is required'),
    checkInEnd: yup.number().min(0).max(60).required('Check-in end is required'),
    shiftLength: yup.number().min(1).required('Minimum shift length is required'),
    checkoutValidUntil: yup.number().min(0).max(60).required('Checkout valid until is required'), // New field
});

const TimeKeeping = ({providerCompanyId}) => {
    const { providerCompanyData, fetchProviderCompanyData } = useProviderCompany(); // Use providerCompanyData from context
    const [isEditing, setIsEditing] = useState(false);

    // React Hook Form setup with yup resolver
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(timeKeepingSchema),
        defaultValues: {
            checkInStart: 0, // Initial default values will be reset with actual data
            checkInEnd: 0,
            shiftLength: 0,
            checkoutValidUntil: 0, // New default value
        },
    });

    // Populate the form when providerCompanyData changes
    useEffect(() => {
        if (providerCompanyData && providerCompanyData.preferences?.timeKeeping) {
            reset({
                checkInStart: providerCompanyData?.preferences.timeKeeping.checkInStartsAtMinutesBefore || 0,
                checkInEnd: providerCompanyData?.preferences.timeKeeping.checkInEndsAtMinutesAfter || 0,
                shiftLength: providerCompanyData?.preferences.timeKeeping.minimumShiftLengthHours || 0,
                checkoutValidUntil: providerCompanyData?.preferences.timeKeeping.checkoutValidUntilMinutesAfter || 0, // New field for checkout
            });
        }
    }, [providerCompanyData, reset]);

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            const timeKeepingData = {
                checkInStartsAtMinutesBefore: data.checkInStart,
                checkInEndsAtMinutesAfter: data.checkInEnd,
                minimumShiftLengthHours: data.shiftLength,
                checkoutValidUntilMinutesAfter: data.checkoutValidUntil, // New field
            };

            // Update backend with new time keeping preferences
            await updateProviderTimeKeepingPreferences(providerCompanyData._id, timeKeepingData);

            // Fetch updated provider company data to refresh the context
            fetchProviderCompanyData(providerCompanyId);

            // Switch back to view mode
            setIsEditing(false);
        } catch (error) {
            console.error("Failed to update time keeping preferences:", error);
        }
    };

    // Toggle edit mode
    const toggleEditMode = () => {
        setIsEditing(!isEditing);
        if (!isEditing && providerCompanyData?.preferences?.timeKeeping) {
            // Prepopulate the form when switching to edit mode
            setValue('checkInStart', providerCompanyData.preferences.timeKeeping.checkInStartsAtMinutesBefore || 0);
            setValue('checkInEnd', providerCompanyData.preferences.timeKeeping.checkInEndsAtMinutesAfter || 0);
            setValue('shiftLength', providerCompanyData.preferences.timeKeeping.minimumShiftLengthHours || 0);
            setValue('checkoutValidUntil', providerCompanyData.preferences.timeKeeping.checkoutValidUntilMinutesAfter || 0); // New field
        }
    };

    return (
        <div className="flex items-start bg-white rounded-lg shadow-sm p-4 mb-4">
            <Timer className="text-green-500 mr-5 mt-1" style={{ fontSize: "20px" }} />
            <div className="flex-1">
                {/* Title with Edit button */}
                <h6 className="mb-1 text-sm font-semibold text-brand-blue-dark flex items-center">
                    Time Keeping
                    {!isEditing && (
                        <Pencil
                            onClick={toggleEditMode}
                            className="ml-2 w-4 h-4 cursor-pointer text-gray-500 hover:text-gray-700"
                        />
                    )}
                </h6>

                {/* Display mode or Edit mode */}
                {isEditing ? (
                    <div className="mt-4">
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div className="w-full">
                                <div className="space-y-4">
                                    <div>
                                        <label className="form-label">Check-in starts at (minutes before):</label>
                                        <input
                                            className="form-input"
                                            type="number"
                                            {...register('checkInStart')}
                                        />
                                        {errors.checkInStart && (
                                            <span className="form-error">{errors.checkInStart?.message}</span>
                                        )}
                                    </div>

                                    <div>
                                        <label className="form-label">Check-in ends at (minutes after):</label>
                                        <input
                                            className="form-input"
                                            type="number"
                                            {...register('checkInEnd')}
                                        />
                                        {errors.checkInEnd && (
                                            <span className="form-error">{errors.checkInEnd?.message}</span>
                                        )}
                                    </div>

                                    {/* New checkout valid field */}
                                    <div>
                                        <label className="form-label">Checkout valid until (minutes after shift):</label>
                                        <input
                                            className="form-input"
                                            type="number"
                                            {...register('checkoutValidUntil')}
                                        />
                                        {errors.checkoutValidUntil && (
                                            <span className="form-error">{errors.checkoutValidUntil?.message}</span>
                                        )}
                                    </div>

                                    <div>
                                        <label className="form-label">Minimum shift length (hours):</label>
                                        <input
                                            className="form-input"
                                            type="number"
                                            {...register('shiftLength')}
                                        />
                                        {errors.shiftLength && (
                                            <span className="form-error">{errors.shiftLength?.message}</span>
                                        )}
                                    </div>

                                    <div className="flex gap-2">
                                        <button className="px-4 py-2 bg-brand-blue-dark text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity" type="submit">
                                            Save Time Keeping
                                        </button>
                                        <button
                                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                                            type="button"
                                            onClick={() => setIsEditing(false)} // Cancel button functionality
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div>
                        {/* View mode: Display current values */}
                        <p className="mb-2 text-sm text-gray-600">
                            Check-in starts at {providerCompanyData?.preferences?.timeKeeping?.checkInStartsAtMinutesBefore || 0} minutes before, ends {providerCompanyData?.preferences?.timeKeeping?.checkInEndsAtMinutesAfter || 0} minutes after.
                        </p>
                        <p className="mb-2 text-sm text-gray-600">
                            Check-out valid until {providerCompanyData?.preferences?.timeKeeping?.checkoutValidUntilMinutesAfter || 0} minutes after shift.
                        </p>
                        <p className="mb-2 text-sm text-gray-600">
                            Minimum shift length is {providerCompanyData?.preferences?.timeKeeping?.minimumShiftLengthHours || 0} hours.
                        </p>

                    </div>
                )}
            </div>
        </div>
    );
};

export default TimeKeeping;
